import React, { forwardRef, useState, useRef, useEffect } from 'react'
import {
  Box,
  TextField,
  Autocomplete,
  InputAdornment,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  ListItem,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'
import StopIcon from '@mui/icons-material/Stop'
import TuneIcon from '@mui/icons-material/Tune'
import ClearIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import {
  fetchAutocompleteResults,
  fetchAutocompleteResultsSuccess,
  fetchRecentSearches,
  cancelSearch,
  setSearchQuery,
  setSearchDocument,
  setSearchDocumentSourceUrl,
  setSearchSelectedCitation,
  clearSearchFilters,
  clearDateFilter,
  clearDateSort,
  filteredSearchCitations,
  setFilterModalOpen,
} from '../../../redux/actions/searchAction'
import CompactCountryLanguageSelector from '../../../components/common/CompactCountryLanguageSelector'
import ModeDropdown from '../../../components/common/ModeDropdown'
import ReactGA from 'react-ga4'
import { tracker } from '../../../analytics'
import { debounce } from 'lodash'
import { useSearchTokens } from '../searchDesignTokens'

const CustomListboxComponent = forwardRef(function CustomListboxComponent(
  { isFetchingMore, dk, children, ...rest },
  ref
) {
  return (
    <Paper
      {...rest}
      ref={ref}
      elevation={0}
      sx={{
        borderRadius: '12px',
        mt: 1,
        maxHeight: 300,
        overflow: 'auto',
        backgroundColor: dk?.elevated || '#111111',
        border: `1px solid ${dk?.border || 'rgba(255,255,255,0.08)'}`,
        boxShadow: dk?.shadowHover || '0 8px 40px rgba(0,0,0,0.7)',
      }}
    >
      {children}
      {isFetchingMore && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '12px'
          }}
        >
          <CircularProgress size={20} sx={{ color: dk?.gold || '#c9a96e' }} />
        </Box>
      )}
    </Paper>
  )
})

export const SearchHeader = ({ mode, setMode, onClearSearch, totalResultCount }) => {
  const dispatch = useDispatch()
  const dk = useSearchTokens()
  const listboxRef = useRef()
  const searchQuery = useSelector(state => state.search.searchQuery)
  const isLoadingSearchCitations = useSelector(
    state => state.search.isLoadingSearchCitations
  )
  const autocompleteResults = useSelector(
    state => state.search.autocompleteResults,
    shallowEqual
  )
  const countryPref = useSelector(state => state.user?.countryPreference)
  // Normalize to array for multi-country support
  const countryPreference = Array.isArray(countryPref) ? countryPref : countryPref ? [countryPref] : ['MA']
  const onlyBF = countryPreference.length === 1 && countryPreference[0] === 'BF'
  // Note: searchLanguage removed - backend now queries all available languages

  const [offset, setOffset] = useState(0)
  const [isFetchingMore, setIsFetchingMore] = useState(false)
  const [localInputValue, setLocalInputValue] = useState('')
  const user = useSelector(state => state.auth.user?.user)
  const { t } = useTranslation()
  const [isAutocompleteOpen, setAutocompleteOpen] = useState(false)
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false)
  const isAbrogeFilter = useSelector(state => state.search.isAbrogeFilter)
  const preSearchFilters = useSelector(state => state.search.preSearchFilters) || {}
  const activePreFilterCount = Object.values(preSearchFilters).reduce((acc, modeFilters) => {
    if (!modeFilters || typeof modeFilters !== 'object') return acc
    return acc + Object.values(modeFilters).filter(v =>
      Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined
    ).length
  }, 0)
  const hasActivePreFilters = activePreFilterCount > 0

  useEffect(() => {
    if (searchQuery) {
      setLocalInputValue(searchQuery)
    }
  }, [])

  useEffect(() => {
    if (onlyBF && mode !== 'law') {
      setMode('law')
    }
  }, [onlyBF, mode])

  const seenTitles = new Set()
  const uniqueAutocompleteResults = autocompleteResults.filter(item => {
    if (seenTitles.has(item.title)) {
      return false
    }
    seenTitles.add(item.title)
    return true
  })

  const handleScroll = event => {
    const { scrollTop, scrollHeight, clientHeight } = event.target

    if (scrollTop + clientHeight >= scrollHeight && !isFetchingMore) {
      setIsFetchingMore(true)

      const newOffset = offset + 7
      const previousScrollTop = scrollTop

      dispatch(fetchRecentSearches(user.id, newOffset)).then(results => {
        if (results.length > 0) {
          setOffset(newOffset)
          if (listboxRef.current) {
            listboxRef.current.scrollTop = previousScrollTop
          }
        }
        setIsFetchingMore(false)
      })
    }
  }

  const handleSearch = async () => {
    dispatch(setSearchDocument(null))
    dispatch(setSearchDocumentSourceUrl(null))
    dispatch(setSearchSelectedCitation(null))
    dispatch(clearSearchFilters())
    dispatch(clearDateFilter())
    dispatch(clearDateSort())

    const queryToSearch = localInputValue.trim() || searchQuery.trim()

    if (queryToSearch) {
      dispatch(setSearchQuery(queryToSearch))
      setLocalInputValue(queryToSearch)

      const filters = isAbrogeFilter ? { is_active: true } : null
      await dispatch(filteredSearchCitations(queryToSearch, mode, filters))

      ReactGA.event({
        category: 'Search',
        action: 'User searched for an element',
        label: queryToSearch
      })
      tracker.event({
        category: 'Search',
        action: 'User searched for an element',
        label: queryToSearch
      })
      setOffset(0)
    }
  }

  const handleCancelSearch = () => {
    dispatch(cancelSearch())

    ReactGA.event({
      category: 'Search',
      action: 'User cancelled search',
      label: searchQuery
    })
    tracker.event({
      category: 'Search',
      action: 'User cancelled search',
      label: searchQuery
    })
  }

  const debouncedHandleSearchInputChange = debounce(async value => {
    if (value) {
      setIsAutocompleteLoading(true)
      // await dispatch(fetchAutocompleteResults(value))
      setIsAutocompleteLoading(false)
    }
  }, 1200)

  const handleSearchInputChange = (event, value, reason) => {
    setLocalInputValue(value)

    if (reason !== 'createOption' && reason !== 'reset') {
      if (reason === 'clear' || value === '') {
        dispatch(setSearchQuery(''))
      } else {
        dispatch(fetchAutocompleteResultsSuccess([]))
        debouncedHandleSearchInputChange(value)
        setOffset(0)
      }
    }
  }

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode)
    }
  }

  const handleAutocompleteSelect = async (event, value) => {
    if (value) {
      let selectedValue = ''

      if (typeof value === 'string') {
        selectedValue = value
      } else if (value && value.title) {
        selectedValue = value.title
      }

      setLocalInputValue(selectedValue)
      dispatch(setSearchQuery(selectedValue))
      dispatch(clearSearchFilters())
      dispatch(clearDateFilter())
      dispatch(clearDateSort())

      const filters = isAbrogeFilter ? { is_active: true } : null
      await dispatch(filteredSearchCitations(selectedValue, mode, filters))
    }
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !isAutocompleteOpen) {
      event.preventDefault()
      handleSearch()
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Autocomplete
            key={'search-input'}
            freeSolo
            disabled={isLoadingSearchCitations}
            open={isAutocompleteOpen}
            onOpen={() => setAutocompleteOpen(true)}
            onClose={() => setAutocompleteOpen(false)}
            options={uniqueAutocompleteResults || []}
            getOptionLabel={option => {
              return typeof option === 'string' ? option : (option.title || '')
            }}
            onInputChange={handleSearchInputChange}
            onChange={async (event, value) => {
              await handleAutocompleteSelect(event, value)
              setAutocompleteOpen(false)
            }}
            inputValue={localInputValue}
            renderInput={params => {
              return (
                <TextField
                  {...params}
                  placeholder={t('search_placeholder')}
                  variant='outlined'
                  fullWidth
                  disabled={false}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: isLoadingSearchCitations,
                  }}
                  sx={{
                    input: {
                      fontSize: '16px',
                      padding: '14px 14px 14px 0',
                      color: dk.text1,
                      '&::placeholder': {
                        color: dk.text3,
                        opacity: 1,
                      },
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: dk.inputBg,
                      border: `1px solid ${dk.inputBorder}`,
                      boxShadow: dk.shadowCard,
                      transition: 'all 0.3s ease',
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        backgroundColor: dk.inputBgHover,
                        borderColor: dk.borderHover,
                      },
                      '&.Mui-focused': {
                        borderColor: dk.gold,
                        boxShadow: `0 0 0 1px ${dk.goldBdr}`,
                      }
                    }
                  }}
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position='start' sx={{ ml: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isAutocompleteLoading ? (
                            <CircularProgress size={22} sx={{ color: dk.gold }} />
                          ) : (
                            <SearchIcon sx={{ color: dk.text3, fontSize: 22 }} />
                          )}
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <React.Fragment>
                        {params.InputProps.endAdornment}
                        <Box sx={{ pointerEvents: 'all' }}>
                          <IconButton
                            data-tour="search-send-btn"
                            onClick={isLoadingSearchCitations ? handleCancelSearch : handleSearch}
                            sx={{
                              color: isLoadingSearchCitations ? dk.error : dk.gold,
                              '&:hover': {
                                backgroundColor: isLoadingSearchCitations
                                  ? `${dk.error}14`
                                  : dk.goldSub,
                              },
                              ml: 0.5
                            }}
                          >
                            {isLoadingSearchCitations ? <StopIcon /> : <SendIcon />}
                          </IconButton>
                        </Box>
                      </React.Fragment>
                    )
                  }}
                />
              );
            }}
            ListboxComponent={CustomListboxComponent}
            ListboxProps={{
              onScroll: handleScroll,
              isFetchingMore: isFetchingMore,
              dk: dk,
              ref: listboxRef
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <ListItem
                  key={key}
                  {...otherProps}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: dk.hover,
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 500,
                      color: dk.text1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      pr: 2
                    }}
                  >
                    {option.title}
                  </Typography>
                  <Chip
                    label={t(`mode_${option.mode === 'law' ? 'reg' : option.mode === 'case_law' ? 'jp' : option.mode === 'regulation' ? 'regulation' : 'reg'}`)}
                    size="small"
                    sx={{
                      backgroundColor: dk.goldSub,
                      color: dk.gold,
                      fontSize: '0.75rem',
                      height: '20px',
                      minWidth: '40px',
                      '& .MuiChip-label': {
                        px: 1
                      }
                    }}
                  />
                </ListItem>
              );
            }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'flex-start' },
          alignItems: 'center',
          mb: 1,
          gap: 1,
          px: { xs: 0.5, sm: 1 },
          overflowX: { xs: 'auto', sm: 'visible' },
          '&::-webkit-scrollbar': {
            height: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: dk.scrollThumb,
            borderRadius: '4px',
            '&:hover': {
              background: dk.borderHover,
            },
          },
          scrollbarWidth: 'thin',
          scrollbarColor: `${dk.scrollThumb} transparent`,
          overflowY: 'hidden',
          pb: { xs: 1, sm: 0 },
          pl: { xs: 0.5, sm: 1 },
          pr: { xs: 0.5, sm: 1 },
          scrollBehavior: 'smooth',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Country & language selector */}
        <Box data-tour="search-country-selector" sx={{ display: 'inline-flex' }}>
          <CompactCountryLanguageSelector
            displayMode="icon"
            mode={mode}
            showLabel={false}
            disabled={isLoadingSearchCitations}
          />
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            mx: 0.5,
            backgroundColor: dk.border,
            height: 24,
            alignSelf: 'center',
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            minWidth: { xs: 'min-content', sm: 'auto' },
            flexShrink: 0,
            alignItems: 'center',
          }}
        >
          <Box data-tour="search-mode-selector" sx={{ display: 'inline-flex' }}>
            <ModeDropdown
              value={mode}
              onChange={(e, newMode) => handleModeChange(null, newMode)}
              displayMode="icon"
              multiSelect
              disabled={isLoadingSearchCitations}
            />
          </Box>

          {/* Filter button */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              backgroundColor: dk.border,
              height: 18,
              alignSelf: 'center',
              mx: 0.25,
            }}
          />
          <Tooltip title={t('advanced_filters')} arrow>
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <IconButton
                data-tour="search-filter-btn"
                onClick={() => dispatch(setFilterModalOpen(true))}
                disabled={isLoadingSearchCitations}
                size="small"
                sx={{
                  color: hasActivePreFilters ? dk.gold : dk.text2,
                  backgroundColor: hasActivePreFilters ? dk.goldSub : 'transparent',
                  border: hasActivePreFilters ? `1px solid ${dk.goldBdr}` : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: dk.goldSub,
                    color: dk.gold,
                  },
                  width: 26,
                  height: 26,
                }}
              >
                <TuneIcon sx={{ fontSize: '0.95rem' }} />
              </IconButton>
              {activePreFilterCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -3,
                    right: -3,
                    width: 15,
                    height: 15,
                    borderRadius: '50%',
                    backgroundColor: dk.gold,
                    color: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.55rem',
                    fontWeight: 700,
                  }}
                >
                  {activePreFilterCount}
                </Box>
              )}
            </Box>
          </Tooltip>
        </Box>

        {/* Spacer to push clear to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Clear search */}
        {totalResultCount > 0 && (
          <Tooltip title={t('clear_search_results')} arrow>
            <IconButton
              onClick={onClearSearch}
              size="small"
              sx={{
                color: dk.text2,
                flexShrink: 0,
                width: 26,
                height: 26,
                '&:hover': {
                  backgroundColor: dk.goldSub,
                  color: dk.gold,
                },
              }}
            >
              <ClearIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
        )}

      </Box>
    </Box>
  )
}
export default SearchHeader
